import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Badge,
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";

import {
  BrazilianHolidayPreviewItem,
  importBrazilianHolidays,
  previewBrazilianHolidays,
} from "../../../services/calendarExceptionService";
import { Unit } from "../../../services/unitService";

export type BrazilianHolidaysImportSummary = {
  created: number;
  skipped: number;
  codes: string[];
  year: number;
};

interface ImportBrazilianHolidaysModalProps {
  isOpen: boolean;
  units: Unit[];
  submitting: boolean;
  initialYear?: number;
  /** When true, render panel content without outer Modal chrome (wizard tabs). */
  embedded?: boolean;
  onClose: () => void;
  onSubmittingChange: (value: boolean) => void;
  onImported: (summary: BrazilianHolidaysImportSummary) => void;
}

const currentYear = (): number => new Date().getFullYear();

const resolvePreviewStatus = (
  holiday: BrazilianHolidayPreviewItem
): "new" | "already" | "skip" => {
  if (holiday.has_manual_overlap) {
    return "skip";
  }

  if (holiday.already_imported) {
    return "already";
  }

  return "new";
};

const ImportBrazilianHolidaysModal = ({
  isOpen,
  units,
  submitting,
  initialYear,
  embedded = false,
  onClose,
  onSubmittingChange,
  onImported,
}: ImportBrazilianHolidaysModalProps) => {
  const { t, i18n } = useTranslation();

  const [step, setStep] = useState<"configure" | "preview">("configure");
  const [year, setYear] = useState(String(initialYear ?? currentYear()));
  const [unitId, setUnitId] = useState("");
  const [includeOptionalDays, setIncludeOptionalDays] = useState(false);
  const [holidays, setHolidays] = useState<BrazilianHolidayPreviewItem[]>([]);
  const [selectedCodes, setSelectedCodes] = useState<string[]>([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setStep("configure");
    setYear(String(initialYear ?? currentYear()));
    setUnitId("");
    setIncludeOptionalDays(false);
    setHolidays([]);
    setSelectedCodes([]);
    setError(null);
  }, [initialYear, isOpen]);

  const holidayName = (item: BrazilianHolidayPreviewItem): string => {
    const key = `studio.settings.calendar_exceptions.brHolidays.names.${item.code}`;
    const translated = t(key);

    return translated === key ? item.name : translated;
  };

  const formatDate = (date: string): string => {
    const [y, m, d] = date.split("-").map(Number);
    if (!y || !m || !d) {
      return date;
    }

    return new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" }).format(
      new Date(y, m - 1, d)
    );
  };

  const selectedCount = selectedCodes.length;

  const allSelected = useMemo(
    () => holidays.length > 0 && selectedCodes.length === holidays.length,
    [holidays.length, selectedCodes.length]
  );

  const statusBadge = (status: "new" | "already" | "skip") => {
    if (status === "skip") {
      return (
        <Badge color="warning" className="ms-1">
          {t("studio.settings.calendar_exceptions.brHolidays.preview.status.skip")}
        </Badge>
      );
    }

    if (status === "already") {
      return (
        <Badge color="info" className="ms-1">
          {t("studio.settings.calendar_exceptions.brHolidays.preview.status.already")}
        </Badge>
      );
    }

    return (
      <Badge color="success" className="ms-1">
        {t("studio.settings.calendar_exceptions.brHolidays.preview.status.new")}
      </Badge>
    );
  };

  const loadPreview = async () => {
    const parsedYear = Number(year);

    if (!Number.isInteger(parsedYear) || parsedYear < 2000 || parsedYear > 2100) {
      setError(t("studio.settings.calendar_exceptions.brHolidays.validation.yearInvalid"));
      return;
    }

    setLoadingPreview(true);
    setError(null);

    try {
      const response = await previewBrazilianHolidays({
        year: parsedYear,
        unit_id: unitId ? Number(unitId) : undefined,
        include_optional_days: includeOptionalDays,
      });

      const items = response.data.holidays;
      setHolidays(items);
      setSelectedCodes(
        items.filter((item) => !item.has_manual_overlap).map((item) => item.code)
      );
      setStep("preview");
    } catch (err: any) {
      setError(err?.data || t("studio.settings.calendar_exceptions.brHolidays.toast.previewError"));
    } finally {
      setLoadingPreview(false);
    }
  };

  const toggleCode = (code: string) => {
    setSelectedCodes((current) =>
      current.includes(code) ? current.filter((item) => item !== code) : [...current, code]
    );
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelectedCodes([]);
      return;
    }

    setSelectedCodes(holidays.map((item) => item.code));
  };

  const confirmImport = async () => {
    if (selectedCodes.length === 0) {
      setError(t("studio.settings.calendar_exceptions.brHolidays.validation.selectAtLeastOne"));
      return;
    }

    onSubmittingChange(true);
    setError(null);

    try {
      const response = await importBrazilianHolidays({
        year: Number(year),
        unit_id: unitId ? Number(unitId) : null,
        include_optional_days: includeOptionalDays,
        codes: selectedCodes,
      });

      onImported({
        created: response.data.created,
        skipped: response.data.skipped.length,
        codes: selectedCodes,
        year: Number(year),
      });
      onClose();
    } catch (err: any) {
      setError(err?.data || t("studio.settings.calendar_exceptions.brHolidays.toast.importError"));
    } finally {
      onSubmittingChange(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  const body = (
    <>
        {error ? (
          <Alert color="danger" className="mb-3">
            {error}
          </Alert>
        ) : null}

        {step === "configure" ? (
          <>
            <p className="text-muted mb-3">
              {t("studio.settings.calendar_exceptions.brHolidays.modal.description")}
            </p>
            <FormGroup>
              <Label for="br-holidays-year">
                {t("studio.settings.calendar_exceptions.brHolidays.fields.year")}
              </Label>
              <Input
                id="br-holidays-year"
                type="number"
                min={2000}
                max={2100}
                value={year}
                onChange={(event) => setYear(event.target.value)}
              />
            </FormGroup>
            <FormGroup>
              <Label for="br-holidays-unit">
                {t("studio.settings.calendar_exceptions.fields.unit")}
              </Label>
              <Input
                id="br-holidays-unit"
                type="select"
                value={unitId}
                onChange={(event) => setUnitId(event.target.value)}
              >
                <option value="">
                  {t("studio.settings.calendar_exceptions.fields.allUnits")}
                </option>
                {units.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.name}
                  </option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup check className="mb-0">
              <Input
                id="br-holidays-optional"
                type="checkbox"
                checked={includeOptionalDays}
                onChange={(event) => setIncludeOptionalDays(event.target.checked)}
              />
              <Label check for="br-holidays-optional">
                {t("studio.settings.calendar_exceptions.brHolidays.fields.includeOptional")}
              </Label>
              <div className="form-text">
                {t("studio.settings.calendar_exceptions.brHolidays.fields.includeOptionalHelp")}
              </div>
            </FormGroup>
          </>
        ) : (
          <>
            <p className="text-muted mb-2">
              {t("studio.settings.calendar_exceptions.brHolidays.preview.hint", { year })}
            </p>
            <div className="d-flex flex-wrap gap-2 mb-3">
              <Badge color="success">
                {t("studio.settings.calendar_exceptions.brHolidays.preview.status.new")}
              </Badge>
              <span className="small text-muted me-2">
                {t("studio.settings.calendar_exceptions.brHolidays.preview.legend.new")}
              </span>
              <Badge color="info">
                {t("studio.settings.calendar_exceptions.brHolidays.preview.status.already")}
              </Badge>
              <span className="small text-muted me-2">
                {t("studio.settings.calendar_exceptions.brHolidays.preview.legend.already")}
              </span>
              <Badge color="warning">
                {t("studio.settings.calendar_exceptions.brHolidays.preview.status.skip")}
              </Badge>
              <span className="small text-muted">
                {t("studio.settings.calendar_exceptions.brHolidays.preview.legend.skip")}
              </span>
            </div>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Button color="link" className="p-0" onClick={toggleAll}>
                {allSelected
                  ? t("studio.settings.calendar_exceptions.brHolidays.preview.unselectAll")
                  : t("studio.settings.calendar_exceptions.brHolidays.preview.selectAll")}
              </Button>
              <span className="text-muted small">
                {t("studio.settings.calendar_exceptions.brHolidays.preview.selectedCount", {
                  count: selectedCount,
                })}
              </span>
            </div>
            <div className="border rounded p-2" style={{ maxHeight: 320, overflowY: "auto" }}>
              {holidays.map((holiday) => {
                const status = resolvePreviewStatus(holiday);

                return (
                  <FormGroup
                    check
                    key={holiday.code}
                    className={`mb-2 p-2 rounded ${
                      status === "skip"
                        ? "bg-warning-subtle"
                        : status === "already"
                          ? "bg-info-subtle"
                          : "bg-success-subtle"
                    }`}
                  >
                    <Input
                      id={`br-holiday-${holiday.code}`}
                      type="checkbox"
                      checked={selectedCodes.includes(holiday.code)}
                      onChange={() => toggleCode(holiday.code)}
                    />
                    <Label check for={`br-holiday-${holiday.code}`} className="w-100">
                      <div className="d-flex flex-wrap justify-content-between gap-2">
                        <span>
                          {holidayName(holiday)}
                          {statusBadge(status)}
                          {holiday.optional ? (
                            <span className="text-muted ms-1">
                              ({t("studio.settings.calendar_exceptions.brHolidays.preview.optional")})
                            </span>
                          ) : null}
                        </span>
                        <span className="text-muted">{formatDate(holiday.date)}</span>
                      </div>
                      {status === "already" ? (
                        <small className="text-info d-block">
                          {t("studio.settings.calendar_exceptions.brHolidays.preview.alreadyImported")}
                        </small>
                      ) : null}
                      {status === "skip" ? (
                        <small className="text-warning d-block">
                          {t("studio.settings.calendar_exceptions.brHolidays.preview.manualOverlap")}
                        </small>
                      ) : null}
                    </Label>
                  </FormGroup>
                );
              })}
            </div>
          </>
        )}
    </>
  );

  const footer = (
    <div className="d-flex flex-wrap justify-content-end gap-2">
      {step === "preview" ? (
        <Button color="light" disabled={submitting} onClick={() => setStep("configure")}>
          {t("studio.settings.calendar_exceptions.brHolidays.actions.back")}
        </Button>
      ) : (
        <Button color="light" onClick={onClose}>
          {t("studio.settings.calendar_exceptions.actions.cancel")}
        </Button>
      )}
      {step === "configure" ? (
        <Button color="primary" disabled={loadingPreview} onClick={loadPreview}>
          {loadingPreview ? <Spinner size="sm" className="me-1" /> : null}
          {t("studio.settings.calendar_exceptions.brHolidays.actions.preview")}
        </Button>
      ) : (
        <Button color="primary" disabled={submitting || selectedCount === 0} onClick={confirmImport}>
          {submitting ? <Spinner size="sm" className="me-1" /> : null}
          {t("studio.settings.calendar_exceptions.brHolidays.actions.confirm")}
        </Button>
      )}
    </div>
  );

  if (embedded) {
    return (
      <div>
        {body}
        <div className="mt-3">{footer}</div>
      </div>
    );
  }

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose}>
        {t("studio.settings.calendar_exceptions.brHolidays.modal.title")}
      </ModalHeader>
      <ModalBody>{body}</ModalBody>
      <ModalFooter>{footer}</ModalFooter>
    </Modal>
  );
};

export default ImportBrazilianHolidaysModal;
